import PerText from '../../../components/custom_components/Text'
import { useTranslation } from 'react-i18next'
import { Divider, Table } from 'antd'
import { FC } from 'react'
import { TargetBonusDto } from '../../../types'
import { useAmountAndCurrency } from '../../../shared/useAmountAndCurrency'
import { useDateTime } from '../../../shared/useDateTime'
import { SortOrder } from 'antd/lib/table'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import useCompleteTargetBonusHistory from '../../../graphql/contractHistory/hooks/useCompleteTargetBonusHistory'
import { TimeZoneAndLocale } from '../../../types.custom'

interface BonusHistoryTableProps {
  employeeId: string
  noDivider?: boolean
  resigned?: boolean
  timezone: TimeZoneAndLocale
}

export const BonusHistoryTable: FC<BonusHistoryTableProps> = ({ employeeId, noDivider = false, resigned, timezone }) => {
  const { formatDateShort } = useDateTime(timezone)
  const { t } = useTranslation()
  const { formatCurrency } = useAmountAndCurrency(timezone.locale)

  // load Data
  const { data, loading, error } = useCompleteTargetBonusHistory(employeeId, resigned)

  if (loading || error || !data) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
  }

  const targetBonus: TargetBonusDto[] = data.targetBonusByEmployeeId.targetBonusHistory
  const showTargetBonusData = !!targetBonus && targetBonus.length > 0

  const bonusColumns = [
    {
      key: 'bonusHistory-amount',
      title: t('profile.contract.bonus.amount'),
      className: 'minWidth-column-m textAlign-right',
      sorter: (amountA: TargetBonusDto, amountB: TargetBonusDto) => {
        const a = amountA.amount ? amountA.amount : 0
        const b = amountB.amount ? amountB.amount : 0
        return a - b
      },
      render: (row: TargetBonusDto) => <PerText>{formatCurrency(row.amount, row.currency)}</PerText>,
    },
    {
      key: 'bonusHistory-placeholder',
      className: 'minWidth-column-m',
    },
    {
      key: 'bonusHistory-placeholder2',
      className: 'minWidth-column-xxs',
    },
    {
      key: 'bonusHistory-fromDate',
      title: t('profile.contract.bonus.fromDate'),
      className: 'minWidth-column-s',
      dataIndex: 'fromDate',
      defaultSortOrder: 'descend' as SortOrder,
      sorter: (dateA: TargetBonusDto, dateB: TargetBonusDto) => {
        const a = dateA.fromDate ? dateA.fromDate : ' '
        const b = dateB.fromDate ? dateB.fromDate : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex: string) => <PerText>{dataIndex && formatDateShort(dataIndex)}</PerText>,
    },
    {
      key: 'bonusHistory-toDate',
      title: t('profile.contract.bonus.toDate'),
      className: 'minWidth-column-s',
      dataIndex: 'toDate',
      sorter: (dateA: TargetBonusDto, dateB: TargetBonusDto) => {
        const a = dateA.toDate ? dateA.toDate : ' '
        const b = dateB.toDate ? dateB.toDate : ' '
        return a.localeCompare(b)
      },
      render: (dataIndex: string) => <PerText>{dataIndex && formatDateShort(dataIndex)}</PerText>,
    },
    {
      key: 'bonusHistory-note',
      title: t('profile.contract.salary.note'),
      className: 'minWidth-column-xl',
      render: (row: TargetBonusDto) => (
        <>
          {row.isTentative && <PerText>{t('profile.contract.bonus.isTentative')}</PerText>}
          {row.isTentative && row.note && <br />}
          {row.note && <PerText>{row.note}</PerText>}
        </>
      ),
    },
  ]

  return (
    <>
      {showTargetBonusData && (
        <>
          {!noDivider && <Divider />}
          <PerText appearance="tile-heading" mb="s">
            {t('profile.contract.bonus.header')}
          </PerText>
          <Table
            dataSource={targetBonus ? targetBonus.map((bonusHistory) => ({ ...bonusHistory, key: bonusHistory.id })) : []}
            columns={bonusColumns}
            className="small"
            pagination={false}
          />
        </>
      )}
    </>
  )
}
